"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Loader, Send, CircleCheck } from "lucide-react";
const services = ["AI Consultation", "AI Enablement", "AI Workflow Automation", "AI Operating System", "Other"];
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="size-16 bg-accent/20 text-ring rounded-lg flex items-center justify-center mb-4">
          <CircleCheck className="size-8" />
        </div>
        <h3 className="text-2xl font-default font-semibold text-foreground">Strategy Request Received</h3>
        <p className="text-muted-foreground max-w-md">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 border-ring text-ring hover:bg-ring/5 rounded-md">
          Send Another Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <FormField control={control} name="name" rules={{
      required: "Name is required",
      minLength: {
        value: 2,
        message: "Name must be at least 2 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium font-default">Full Name</FormLabel>
            <FormControl>
              <Input placeholder="Alex Thompson" className="bg-background border-border focus-visible:ring-ring h-12 rounded-md shadow-sm" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium font-default">Email Address</FormLabel>
            <FormControl>
              <Input type="email" placeholder="alex@company.com" className="bg-background border-border focus-visible:ring-ring h-12 rounded-md shadow-sm" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="service" rules={{
      required: "Please select a service type"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium font-default">Service Interest</FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger className="bg-background border-border focus-visible:ring-ring h-12 rounded-md shadow-sm">
                  <SelectValue placeholder="Select a service..." />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {services.map((service, i) => <SelectItem key={i} value={service} data-index={i}>
                    {service}
                  </SelectItem>)}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required",
      minLength: {
        value: 10,
        message: "Message must be at least 10 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium font-default">Workflow Challenges</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about the repetitive tasks or workflow bottlenecks you'd like to solve with AI." className="bg-background border-border focus-visible:ring-ring min-h-[150px] resize-y rounded-md shadow-sm" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md text-destructive text-sm">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full bg-ring hover:bg-secondary text-white shadow-md h-14 text-lg font-default font-semibold rounded-md transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Processing...
          </> : <>
            Build My AI Strategy
            <Send className="ml-2 size-5" />
          </>}
      </Button>
    </Form>;
}
export default function LetSBuildYourAiStrategy() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="253662" id="lets-build-your-ai-strategy" className="bg-background text-foreground py-20 md:py-32 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-lg bg-ring/5 blur-3xl" />
        <div className="absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] rounded-lg bg-secondary/5 blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div ref={ref} className={`max-w-3xl mx-auto transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-default font-bold text-foreground mb-4 tracking-tight">
              Let's Build Your AI Strategy
            </h2>
            <p className="text-muted-foreground text-lg max-w-xl mx-auto font-default">
              Ready to turn AI into measurable business outcomes? Share your challenges and our consultants will design a custom roadmap for your SMB.
            </p>
          </div>

          <Card className="border border-border shadow-xl bg-card rounded-xl overflow-hidden backdrop-blur-sm">
            <CardContent className="p-8 md:p-10">
              <FormProvider formKey="ai_strategy_form_connect" formId="ai_strategy_form_connect" sectionName="Contact Section" defaultValues={{
              name: "",
              email: "",
              service: "",
              message: ""
            }} submitText="Build My AI Strategy" successMessage="Thank you for reaching out. Our AI strategy team will review your inquiry and contact you within 24 hours to schedule a consultation.">
                <ContactFormInner />
              </FormProvider>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>;
}